export type LegalSection = {
  heading: string
  paragraphs: string[]
}

export type LegalDocument = {
  id: 'privacidad' | 'terminos'
  label: string
  title: string
  updatedAt: string
  sections: LegalSection[]
}

export const legalDocuments: LegalDocument[] = [
  {
    id: 'privacidad',
    label: 'Política de Privacidad',
    title: 'Política de Privacidad',
    updatedAt: 'Última actualización: enero de 2026',
    sections: [
      {
        heading: 'Qué información recopilamos',
        paragraphs: [
          'Recopilamos únicamente la información que tú nos proporcionas al realizar tu compra: nombre y correo electrónico para poder entregarte la guía digital y sus materiales.',
          'Nunca te pedimos ni almacenamos tu número de Seguro Social, tus reportes de crédito, tus contraseñas bancarias ni ningún dato de tus cuentas financieras. Las disputas las envías tú directamente a los burós.',
        ],
      },
      {
        heading: 'Datos de pago',
        paragraphs: [
          'Los pagos se procesan a través de una plataforma externa de pago segura. No vemos, no recibimos ni guardamos los números completos de tu tarjeta de crédito o débito.',
        ],
      },
      {
        heading: 'Cómo usamos tu información',
        paragraphs: [
          'Usamos tu correo electrónico para enviarte el acceso a tu compra, actualizaciones del material y comunicaciones relacionadas con el producto.',
          'No vendemos, alquilamos ni intercambiamos tu información personal con terceros para fines publicitarios.',
        ],
      },
      {
        heading: 'Cookies y analítica',
        paragraphs: [
          'Este sitio puede usar cookies y herramientas de medición para entender cómo se navega la página y mejorar la experiencia de compra. Puedes desactivar las cookies desde la configuración de tu navegador en cualquier momento.',
        ],
      },
      {
        heading: 'Tus derechos',
        paragraphs: [
          'Puedes solicitar en cualquier momento el acceso, la corrección o la eliminación de los datos que tenemos sobre ti, así como darte de baja de nuestros correos con el enlace incluido en cada mensaje.',
        ],
      },
      {
        heading: 'Seguridad',
        paragraphs: [
          'Aplicamos medidas razonables para proteger la información almacenada. Ningún sistema en internet es infalible, por lo que no podemos garantizar seguridad absoluta.',
        ],
      },
    ],
  },
  {
    id: 'terminos',
    label: 'Términos y Condiciones',
    title: 'Términos y Condiciones',
    updatedAt: 'Última actualización: enero de 2026',
    sections: [
      {
        heading: 'Naturaleza del producto',
        paragraphs: [
          '“Crédito Limpio en 30 Días” es un producto digital educativo: una guía, plantillas de cartas y checklists descargables. No somos una agencia de reparación de crédito, un despacho legal ni una institución financiera.',
          'Nada de lo incluido constituye asesoría legal, fiscal o de inversión. Las decisiones que tomes con tu crédito son tuyas y bajo tu responsabilidad.',
        ],
      },
      {
        heading: 'Licencia de uso',
        paragraphs: [
          'Tu compra te otorga una licencia personal, intransferible y de por vida para usar los materiales. Está prohibido revenderlos, distribuirlos, publicarlos o compartirlos total o parcialmente sin autorización escrita.',
        ],
      },
      {
        heading: 'Entrega y acceso',
        paragraphs: [
          'La entrega es digital e inmediata tras confirmarse el pago. El acceso se envía al correo electrónico registrado en la compra, por lo que es tu responsabilidad ingresarlo correctamente.',
        ],
      },
      {
        heading: 'Pagos y reembolsos',
        paragraphs: [
          'El precio es un pago único, sin mensualidades ni cargos recurrentes. Por tratarse de un producto digital descargable de acceso inmediato, las ventas son finales una vez entregado el material, salvo que la ley aplicable disponga lo contrario.',
        ],
      },
      {
        heading: 'Sin garantía de resultados',
        paragraphs: [
          'Los resultados dependen de tu historial, de la información que aparezca en tu reporte y de la respuesta de cada buró de crédito. Los ejemplos y cifras mencionadas son referenciales y no constituyen una promesa de puntaje, aprobación ni eliminación de cuentas.',
          'El uso de la guía debe hacerse siempre dentro del marco de la ley FCRA. No apoyamos ni promovemos la evasión de deudas legítimas ni la presentación de información falsa.',
        ],
      },
      {
        heading: 'Limitación de responsabilidad',
        paragraphs: [
          'En la máxima medida permitida por la ley, nuestra responsabilidad total se limita al monto que pagaste por el producto. No respondemos por daños indirectos, pérdidas de oportunidad o decisiones financieras tomadas a partir del material.',
        ],
      },
      {
        heading: 'Cambios en los términos',
        paragraphs: [
          'Podemos actualizar estos términos para reflejar mejoras del producto o cambios legales. La versión vigente será siempre la publicada en esta página.',
        ],
      },
    ],
  },
]

const gluteOverrides: Record<string, string[]> = {
  'Qué información recopilamos': [
    'Recopilamos únicamente la información que tú nos proporcionas al realizar tu compra: nombre y correo electrónico para poder entregarte la guía digital y sus materiales.',
    'No te pedimos ni almacenamos datos médicos, historiales clínicos ni información financiera de tus cuentas.',
  ],
  'Naturaleza del producto': [
    '“Glute Lab: El Código del Crecimiento” es un producto digital educativo: una guía en PDF, protocolos de activación, un plan de entrenamiento y material de nutrición descargables.',
    'No somos médicos, fisioterapeutas ni nutricionistas, y el contenido no sustituye una evaluación profesional de tu salud. Consulta a tu médico antes de iniciar cualquier programa de ejercicio.',
  ],
  'Sin garantía de resultados': [
    'Los resultados dependen de tu punto de partida, tu constancia, tu descanso y tu alimentación. Los ejemplos y tiempos mencionados son referenciales y no constituyen una promesa de resultados físicos específicos.',
    'Entrena dentro de tus posibilidades y detén cualquier ejercicio que te cause dolor. El uso del material es bajo tu propia responsabilidad.',
  ],
  'Limitación de responsabilidad': [
    'En la máxima medida permitida por la ley, nuestra responsabilidad total se limita al monto que pagaste por el producto. No respondemos por lesiones, daños indirectos ni decisiones tomadas a partir del material.',
  ],
}

export const gluteLegalDocuments: LegalDocument[] = legalDocuments.map((doc) => ({
  ...doc,
  sections: doc.sections.map((section) =>
    gluteOverrides[section.heading]
      ? { ...section, paragraphs: gluteOverrides[section.heading] }
      : section,
  ),
}))
